import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {useMemo} from 'react';

import * as Filters from '../../../util/filters';
import type {Filter} from '../../../util/filterTypes';
import {
  useRampFlagBaselineRuns,
  useRampFlagPinnedRuns,
} from '../../../util/rampFeatureFlags';

const RUN_NAME = Filters.Key('run', 'name');

/**
 * Converts a fully-qualfied run ID to a run name, which is used as the run's
 * concrete identifier for the purposes of filtering. If the ID is invalid,
 * the function catches the error and returns null.
 *
 * @deprecated Use in unit tests only please.
 * @param id Fully-qualified run ID
 * @return Run name, or null if the ID is invalid
 */
export function convertRunIdToName(id: string): string | null {
  if (!id) {
    return null;
  }
  try {
    return atob(id).split(':')[2] ?? null;
  } catch (e) {
    return null;
  }
}

/**
 * Generates a filter that excludes pinned and baseline runs. This is important
 * because the main run selector query should not return runs that are already
 * pinned or set as the baseline, because those are queried separately.
 *
 * @deprecated Use in unit tests only please.
 * @param pinnedRunIds
 * @param baselineRunId
 * @return Filter that excludes pinned and baseline runs, or null if there
 *         are no pinned or baseline runs
 */
export function generateExcludedIdsFilter(
  pinnedRunIds: string[],
  baselineRunId?: string
): Filter<any> | null {
  if (pinnedRunIds.length === 0 && !baselineRunId) {
    return null;
  }
  const excludedIds = baselineRunId
    ? [baselineRunId, ...pinnedRunIds]
    : pinnedRunIds;

  return Filters.NotIn(
    RUN_NAME,
    excludedIds.map(convertRunIdToName).filter(isNotNullOrUndefined)
  );
}

/**
 * Generates the filter that selects any pinned or baseline or pinned ids
 * passed to the function, returning an empty Or clause if it's given no ids.
 *
 * @deprecated Use in unit tests only please.
 * @param pinnedRunIds
 * @param baselineRunId
 * @return Filter that selects pinned and baseline runs, returning an empty
 *         Or clause if no ids are passed.
 */
export function generatePinnedIdsFilter(
  pinnedRunIds: string[],
  baselineRunId?: string
): Filter<any> {
  if (pinnedRunIds.length === 0 && !baselineRunId) {
    return Filters.Or([]);
  }
  const pinnedIds = baselineRunId
    ? [baselineRunId, ...pinnedRunIds]
    : pinnedRunIds;

  return Filters.Or(
    pinnedIds
      .map(convertRunIdToName)
      .filter(isNotNullOrUndefined)
      .map(name => Filters.Eq(RUN_NAME, name))
  );
}

export function usePinnedRunFilters(
  pinnedRunIds: string[],
  baselineRunId?: string
) {
  const isBaselineEnabled = useRampFlagBaselineRuns();
  const isPinnedRunsEnabled = useRampFlagPinnedRuns();

  const excludedIDsFilter = useMemo(() => {
    return generateExcludedIdsFilter(
      isPinnedRunsEnabled ? pinnedRunIds : [],
      isPinnedRunsEnabled && isBaselineEnabled ? baselineRunId : undefined
    );
  }, [baselineRunId, isBaselineEnabled, isPinnedRunsEnabled, pinnedRunIds]);

  const pinnedRunsFilter = useMemo(() => {
    return generatePinnedIdsFilter(
      isPinnedRunsEnabled ? pinnedRunIds : [],
      isPinnedRunsEnabled && isBaselineEnabled ? baselineRunId : undefined
    );
  }, [baselineRunId, isBaselineEnabled, isPinnedRunsEnabled, pinnedRunIds]);

  return {
    excludedIDsFilter,
    pinnedRunsFilter,
  };
}
